import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  Placement,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import { useRef, useState } from 'react';
import { ChevronDown } from '../../../assets/icons';
import { Button, ButtonSize, ButtonType } from '../Button.component';
import { Label, LabelType } from '../Label.component';
import { ColorType } from '../shared/Colors.g';

export interface SplitButtonOption {
  label: string;
  onClick: () => void;
  icon?: string;
  iconColor?: ColorType;
  disabled?: boolean;
  dataTestId?: string;
}

interface SplitButtonProps {
  /**
   * Actions to choose from. The primary button mirrors the selected option;
   * picking from the caret menu changes the selection without executing.
   * Clicking the primary executes the selected option. Expects 2 or more.
   */
  options: SplitButtonOption[];
  defaultSelectedIndex?: number;
  type?: ButtonType;
  size?: ButtonSize;
  caretIcon?: string;
  placement?: Placement;
  width?: string | number;
  menuMinWidth?: number;

  disabled?: boolean;
  dataTestId?: string;
}

export const SplitButton = ({
  options,
  defaultSelectedIndex = 0,
  type = ButtonType.OUTLINE,
  size = ButtonSize.MINI,
  caretIcon = ChevronDown,
  placement = 'bottom-end',
  width = '100%',
  menuMinWidth = 160,
  disabled = false,
  dataTestId = 'split-button',
}: SplitButtonProps) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(defaultSelectedIndex);
  const listRef = useRef<Array<HTMLElement | null>>([]);

  const selected = options[selectedIndex] ?? options[0];

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    middleware: [offset(4), flip()],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'menu' });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    loop: true,
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    click,
    dismiss,
    role,
    listNav,
  ]);

  const select = (index: number) => {
    setSelectedIndex(index);
    setOpen(false);
  };

  return (
    <>
      <div className='flex' style={{ width }}>
        {/* Primary — reflects the selected option; click runs it. */}
        <Button
          label={selected.label}
          type={type}
          icon={selected.icon}
          iconColor={selected.iconColor}
          size={size}
          onClick={() => selected.onClick()}
          disabled={disabled || !!selected.disabled}
          dataTestId={dataTestId}
          buttonStyle={{
            borderWidth: 1,
            flex: 1,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          }}
          labelContainerCss='label-flex-g-0'
        />

        {/* Caret — opens the selection menu. */}
        <div ref={refs.setReference} {...getReferenceProps()}>
          <Button
            label=''
            icon={caretIcon}
            type={type}
            iconColor={ColorType.GREY5}
            size={size}
            disabled={disabled}
            dataTestId={`${dataTestId}-caret`}
            labelContainerCss='hidden'
            buttonStyle={{
              borderWidth: 1,
              borderLeftWidth: 0,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              width: 32,
              minWidth: 32,
              paddingLeft: 0,
              paddingRight: 0,
              gap: 0,
            }}
          />
        </div>
      </div>

      {open && !disabled && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={{ ...floatingStyles, minWidth: menuMinWidth }}
              {...getFloatingProps()}
              className='z-50 flex flex-col bg-white outline-0 rounded-lg list-shadow p-2'
              data-testid={`${dataTestId}-menu`}
            >
              {options.map((option, index) => {
                const isActive = activeIndex === index;
                return (
                  <button
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${option.label}-${index}`}
                    ref={(node) => {
                      listRef.current[index] = node;
                    }}
                    type='button'
                    role='menuitem'
                    tabIndex={isActive ? 0 : -1}
                    disabled={option.disabled}
                    data-testid={option.dataTestId ?? 'split-button-option'}
                    className={`pl-2 pr-2 min-h-[40px] text-left w-full rounded ${
                      isActive ? 'bg-grey1' : ''
                    } ${option.disabled ? 'cursor-not-allowed' : ''}`}
                    {...getItemProps({ onClick: () => select(index) })}
                  >
                    <Label
                      type={option.disabled ? LabelType.BODY3_DISABLED : LabelType.BODY3}
                      text={option.label}
                      color={isActive ? ColorType.BRAND2 : ColorType.BLACK}
                      className='truncate'
                    />
                  </button>
                );
              })}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

SplitButton.displayName = 'SplitButton';
